import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { shared } from '../styles/shared.js';
import { LocaleController } from '../i18n/locale-controller.js';
import { t } from '../i18n/t.js';
import { navigate } from '../router.js';

/**
 * Shown when a 5-trial session ends by reaching the trial cap.
 *
 * Promotion is celebrated (level-up callout). **Demotion is silent** —
 * `promoted=false, demoted=true` renders identically to a stay-at-level
 * outcome (positive stars-earned only).
 */
@customElement('session-summary')
export class SessionSummary extends LitElement {
  static override styles = [
    shared,
    css`
      :host {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--space-6);
        height: 100%;
        padding: var(--space-6);
        text-align: center;
      }

      .stars {
        font-size: 96px;
        line-height: 1;
      }

      .level-up {
        background: var(--color-accent-yellow);
        padding: var(--space-3) var(--space-5);
        border-radius: var(--radius-pill);
        font-family: var(--font-display);
        font-weight: 700;
        font-size: var(--type-l);
      }

      button {
        font-size: var(--type-m);
      }

      @media (max-height: 500px) {
        :host {
          gap: var(--space-3);
          padding: var(--space-3);
        }

        .stars {
          font-size: 56px;
        }

        .level-up {
          font-size: var(--type-m);
          padding: var(--space-2) var(--space-4);
        }

        button {
          font-size: var(--type-s);
        }
      }
    `,
  ];

  @property({ type: Number }) starsEarned = 0;
  @property({ type: Boolean }) promoted = false;
  /** Demotion is recorded for state but is INTENTIONALLY not rendered. */
  @property({ type: Boolean, attribute: 'data-demoted' }) demoted = false;

  private locale = new LocaleController(this);

  private onContinue(): void {
    this.dispatchEvent(new CustomEvent('summary-continue', { bubbles: true, composed: true }));
    navigate('home');
  }

  override render() {
    const loc = this.locale.locale;
    const starsLabel = t(loc, 'summary.stars', { count: this.starsEarned });
    return html`<h1 class="text-display">${t(loc, 'summary.title')}</h1>
      <div class="stars" data-testid="stars" aria-label="${starsLabel}">
        ${'⭐'.repeat(this.starsEarned)}
      </div>
      <p class="text-body">${starsLabel}</p>
      ${this.promoted
        ? html`<div class="level-up" data-testid="level-up">${t(loc, 'summary.levelup')}</div>`
        : nothing}
      <button class="button-primary" data-testid="continue" @click=${this.onContinue}>
        ${t(loc, 'summary.continue')}
      </button>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'session-summary': SessionSummary;
  }
}
